// Shared data plumbing for the dashboard's current geography selection.
//
// The KPI header and the metric narrative panel both need the same two rows
// (the selected county and the selected state) and must resolve a metric's
// value the same way — otherwise the two can display different numbers for the
// same selection. This module owns that single code path; neither component
// reads the data service directly.
//
// workforceDataService caches both payloads, so a second consumer of this hook
// costs no additional network requests.
import { useEffect, useState } from 'react';
import { workforceDataService } from './WorkforceDataService';
import { readCountyValue, readStateValue, formatMetricValue } from './metricRegistry';
/**
 * Fetch the county and state rows for the current selection. Re-fetches when
 * the geographic level, selected county, or selected state changes.
 */
export function useSelectionData(dashboardState) {
    const { geoLevel, selectedCounty, selectedState } = dashboardState;
    // The selection the rows below belong to. Data and the key it was fetched
    // for are stored together, so a consumer can never read the PREVIOUS
    // selection's rows as though they described the current one — clicking a
    // county used to render one frame of "no data" for a county that has data.
    const selectionKey = `${geoLevel}|${selectedState}|${selectedCounty !== null && selectedCounty !== void 0 ? selectedCounty : ''}`;
    const [loaded, setLoaded] = useState({ key: null, countyData: null, stateData: null });
    useEffect(() => {
        // Guards against a slow response for a previous selection landing after
        // a newer one and overwriting it.
        let cancelled = false;
        const load = async () => {
            const countyPromise = (geoLevel === 'county' && selectedCounty && selectedState)
                ? workforceDataService.getCountyByName(selectedState, selectedCounty)
                : Promise.resolve(null);
            const statePromise = workforceDataService.getStateByName(selectedState);
            try {
                const [county, state] = await Promise.all([countyPromise, statePromise]);
                if (cancelled)
                    return;
                setLoaded({ key: selectionKey, countyData: county, stateData: state });
            }
            catch (error) {
                console.error('Error fetching workforce data:', error);
                if (cancelled)
                    return;
                setLoaded({ key: selectionKey, countyData: null, stateData: null });
            }
        };
        load();
        return () => {
            cancelled = true;
        };
    }, [selectionKey]);
    // Stale until the fetch for THIS selection lands. Consumers show their
    // loading state rather than describing the wrong geography.
    const isLoading = loaded.key !== selectionKey;
    return {
        countyData: isLoading ? null : loaded.countyData,
        stateData: isLoading ? null : loaded.stateData,
        isLoading
    };
}
/**
 * Read a metric's value for the current selection: the county row when a county
 * is drilled into, otherwise the state row (county view with no county selected
 * falls back to state data, which is what the KPI header has always done).
 */
export function resolveMetricValue(dashboardState, countyData, stateData, metricId) {
    const absent = { raw: null, formatted: 'N/A' };
    if (!dashboardState.selectedCategoryId || !metricId) {
        return absent;
    }
    // County level with a county selected
    if (dashboardState.geoLevel === 'county' && countyData && dashboardState.selectedCounty != null) {
        const raw = readCountyValue(countyData, metricId);
        if (raw !== null && raw !== undefined) {
            return { raw, formatted: formatMetricValue(metricId, raw) };
        }
    }
    // State level, or county view with no county selected (fall back to state data)
    else if ((dashboardState.geoLevel === 'state' && stateData) || (dashboardState.selectedCounty == null && stateData != null)) {
        const raw = readStateValue(stateData, metricId);
        if (raw !== null && raw !== undefined) {
            return { raw, formatted: formatMetricValue(metricId, raw) };
        }
    }
    return absent;
}
